import { Aside } from '@psl/appComponents/Aside';
import {
  adaptiveWrapperOptions,
  ModalWrapper,
} from '@psl/appComponents/ModalWrapper';
import { UserInterfaceIdiomProvider } from '@psl/appComponents/UserInterfaceIdiomContext';
import { useIsMobileLayout } from '@psl/components/styles/responsive';
import { OrderNavParams } from '@psl/flows/orders/OrderScreens';
import { useSettingsContext } from '@psl/flows/settings/SettingsFlowContext';
import { getInitialPath } from '@psl/functions/navigation/linking/initial-url';
import { screenOptionsForLightMode } from '@psl/functions/navigation/screenOptions';
import { wrapLazyNavigatorScreen } from '@psl/functions/navigation/utils/wrapNavigatorScreen';
import { useResponsiveContext } from '@psl/functions/responsive/ResponsiveContext';
import { NavigatorScreenParams, useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { lazy, useLayoutEffect } from 'react';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

const OrdersFlowContent = lazy(() => import('./components/OrdersFlowContent'));

export const OrdersFlow = wrapLazyNavigatorScreen(
  ({
    nonMobileLayout,
    dismissible,
    nested,
  }: NavigatorScreenParams<OrderNavParams> & {
    nested?: boolean;
    nonMobileLayout?: 'modal' | 'aside';
    dismissible?: boolean;
  }) => {
    const isMobile = useIsMobileLayout();
    const { rpx } = useResponsiveContext();
    const { bottom, top } = useSafeAreaInsets();

    const navigation = useNavigation<StackNavigationProp<any>>();

    const settingsContext = useSettingsContext();
    const initialPath = getInitialPath();

    const isNested =
      nested || settingsContext != null || initialPath?.startsWith('/settings');

    useLayoutEffect(() => {
      const parent = navigation.getParent();
      if (isNested) {
        if (parent) {
          parent.setOptions({
            headerShown: false,
          });
          return () => {
            // parent.setOptions({
            //   headerShown: true,
            // });
          };
        }
      }
    }, [isNested, navigation, nonMobileLayout]);

    if (isNested) {
      return <OrdersFlowContent />;
    }

    if (isMobile) {
      return (
        <UserInterfaceIdiomProvider
          name="listing-editor"
          userInterfaceStyle="light"
        >
          <SafeAreaInsetsContext.Provider
            value={{
              left: 0,
              right: 0,
              // note(@zhigang1992): fix react navigation bug on presentation: modal style
              top,
              bottom,
            }}
          >
            <OrdersFlowContent />
          </SafeAreaInsetsContext.Provider>
        </UserInterfaceIdiomProvider>
      );
    } else {
      if (nonMobileLayout === 'modal') {
        return (
          <UserInterfaceIdiomProvider
            name="settings"
            userInterfaceStyle="light"
          >
            <ModalWrapper
              dismissible={dismissible ?? true}
              width={rpx(400)}
              height={rpx(750)}
            >
              <OrdersFlowContent />
            </ModalWrapper>
          </UserInterfaceIdiomProvider>
        );
      } else {
        return (
          <UserInterfaceIdiomProvider
            name="settings"
            userInterfaceStyle="light"
          >
            <Aside dismissible={dismissible ?? true}>
              <OrdersFlowContent />
            </Aside>
          </UserInterfaceIdiomProvider>
        );
      }
    }
  },
  ({ route }) =>
    route.params?.nested
      ? screenOptionsForLightMode({
          headerShown: false,
          cardOverlayEnabled: true,
          cardStyle: { backgroundColor: 'transparent' },
          headerBackTitle: 'Back',
        })
      : adaptiveWrapperOptions(isMobile =>
          isMobile
            ? screenOptionsForLightMode({
                presentation: 'card',
              })
            : screenOptionsForLightMode({
                presentation: 'transparentModal',
                cardOverlayEnabled: true,
                cardStyle: { backgroundColor: 'transparent' },
                animationEnabled: false,
                headerShown: false,
              }),
        ),
);
